import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ExternalLink, FileText, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/shadcn/Badge/Badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/shadcn/Table/Table";
import { automationRunsQueryOptions } from "@/lib/data/react-query/automations";
import { automationError } from "./automationError";

export function AutomationActivity({ id }: { id: string }) {
  const [search, setSearch] = useState("");
  const query = useInfiniteQuery(automationRunsQueryOptions(id));
  const runs = query.data?.pages.flatMap((page) => page.items) ?? [];
  const searchTerm = search.trim().toLocaleLowerCase();
  const visibleRuns = runs.filter((run) =>
    (run.fileName || (run.fileContentId ? "Processed file" : "Deleted file"))
      .toLocaleLowerCase()
      .includes(searchTerm),
  );
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full sm:max-w-sm">
          <Search
            aria-hidden="true"
            className="text-muted-foreground absolute top-3 left-3 size-4"
          />
          <Input
            aria-label="Search documents"
            placeholder={
              query.hasNextPage
                ? "Search loaded documents…"
                : "Search documents…"
            }
            className="pl-9"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
        </div>
        <p className="text-muted-foreground text-xs" role="status">
          {searchTerm ? `${visibleRuns.length} of ` : ""}
          {runs.length}
          {query.hasNextPage ? "+" : ""} documents
        </p>
      </div>
      <div className="h-96 overflow-hidden rounded-xl border [&>div]:h-full [&>div]:[scrollbar-gutter:stable]">
        <Table
          className="min-w-[560px] table-fixed"
          aria-label="Automation runs"
        >
          <colgroup>
            <col />
            <col className="w-36" />
            <col className="w-36" />
          </colgroup>
          <TableHeader className="bg-muted sticky top-0 z-10">
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-10 min-w-0">Document</TableHead>
              <TableHead className="h-10 w-36 min-w-0">Status</TableHead>
              <TableHead className="h-10 w-36 min-w-0">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRuns.map((run) => {
              const completed =
                run.status === "completed" || run.status === "complete";
              const fileName =
                run.fileName ||
                (run.fileContentId ? "Processed file" : "Deleted file");
              return (
                <TableRow key={run.id} className="hover:bg-muted/20">
                  <TableCell>
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="bg-muted/60 text-muted-foreground flex size-11 shrink-0 items-center justify-center rounded-lg">
                        <FileText aria-hidden="true" className="size-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium" title={fileName}>
                          {run.fileContentId ? (
                            <Link
                              to="/file-content/$fileContentId"
                              params={{ fileContentId: run.fileContentId }}
                              className="hover:underline"
                            >
                              {fileName}
                            </Link>
                          ) : (
                            fileName
                          )}
                        </p>
                        <p className="text-muted-foreground mt-1 text-xs">
                          {new Date(run.dateCreated).toLocaleDateString()} ·{" "}
                          {run.triggerType === "manual"
                            ? "Manual run"
                            : "Automatic run"}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={completed ? "green" : "secondary"}
                      className="gap-2 border-0 px-3 py-1 whitespace-nowrap"
                    >
                      <span
                        aria-hidden="true"
                        className="size-2 rounded-full bg-current"
                      />
                      {completed
                        ? "Completed"
                        : run.status === "failed"
                          ? "Failed"
                          : run.status === "cancelled"
                            ? "Cancelled"
                            : run.status === "paused"
                              ? "Paused"
                              : "Pending"}
                    </Badge>
                  </TableCell>
                  <TableCell className="pl-0">
                    <div className="border-l pl-4">
                      {run.threadId &&
                      (completed || run.status === "paused") ? (
                        <Button size="sm" asChild>
                          <Link to="/ai" search={{ thread: run.threadId }}>
                            {completed ? "Open run" : "Review request"}{" "}
                            <ExternalLink aria-hidden="true" />
                          </Link>
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          disabled
                          title="The conversation is available after completion."
                        >
                          Open run <ExternalLink aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {(query.isPending || query.error || !visibleRuns.length) && (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="text-muted-foreground py-8 text-center"
                >
                  {query.isPending ? (
                    <p role="status">Loading activity…</p>
                  ) : query.error ? (
                    <p role="alert" className="text-destructive">
                      {automationError(query.error)}
                    </p>
                  ) : !runs.length ? (
                    "No runs yet. Matching new files will appear here after processing."
                  ) : query.hasNextPage ? (
                    "No matching documents in the loaded runs."
                  ) : (
                    "No matching documents."
                  )}
                </TableCell>
              </TableRow>
            )}
            {query.hasNextPage && (
              <TableRow>
                <TableCell colSpan={3} className="text-center">
                  <Button
                    variant="outline"
                    disabled={query.isFetchingNextPage}
                    onClick={() => void query.fetchNextPage()}
                  >
                    {query.isFetchingNextPage ? "Loading…" : "Older runs"}
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
